import React, { useState, useEffect } from 'react';
import { getOrdersList, getOrdersReport, captureOrder, refundPayment } from '../../axios-client';
import Tables from '../../reusableComponents/Tables/Table/TableCard';
import { formatDataToUrlQueryParams, getStatusIcon, getFormattedTodayDate, getFormattedFiveDaysAgoDate, } from '../../utlis/generalUtils';
import Tooltip from '@mui/material/Tooltip';
import logo from 'assets/images/logo.png';
import { downloadInvoicePDF } from './helper';
import { FaDownload } from 'react-icons/fa6';
import { MdDone, MdOutlineAttachMoney, MdOutlineRemove } from 'react-icons/md';
import { GrInProgress } from 'react-icons/gr';
import { HiOutlineClipboardDocumentCheck } from 'react-icons/hi2';
import { useSoftUIController } from '../../context';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RefundDialog from './RefundDialog';
import SnackbarNotification from '../../reusableComponents/Snackbar/SnackbarNotification';
import Button from '@mui/material/Button';
import {FREE_AGENT, SUPER_ADMIN_ROLE} from '../../utlis/constant';
import { useTranslation } from "react-i18next";
import ServicesIconsGroup from '../../reusableComponents/ServicesIcons/ServicesIconsGroup';

const PaymentList = () => {
  const [controller] = useSoftUIController();
  const { role, profileData } = controller;
  const { t, i18n } = useTranslation(['invoice']);

  const rolesViewCards = [SUPER_ADMIN_ROLE, FREE_AGENT];
  const rolesCanSeeRevenue = [SUPER_ADMIN_ROLE];
  const isFreeAgent = role === FREE_AGENT;

  const [orderData, setOrderData] = useState({
    totalOrders: 0,
    ordersInProcess: 0,
    ordersCompleted: 0,
    totalRevenue: 0,
    percentageChanges: {},
  });
  const [orderDataLoading, setOrderDataLoading] = useState(true);

  // State for the table data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersCount, setOrdersCount] = useState(0);

  // Get today's date for default filter values
  const today = getFormattedTodayDate();
  const fiveDaysAgo = getFormattedFiveDaysAgoDate();

  // State for pagination and filters
  const [filterData, setFilterData] = useState({
    page: 1,
    size: 10,
    from: fiveDaysAgo,
    to: today,
    status: '',
    order_id: '',
    email: '',
  });

  // State for refund dialog and notifications
  const [openDialog, setOpenDialog] = useState(false);
  const [refundType, setRefundType] = useState('custom');
  const [refundAmount, setRefundAmount] = useState(0);
  const [description, setDescription] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    snackbarMessage: '',
    severity: '',
  });

  const [filters, setFilters] = useState([]);

  useEffect(() => {

    const newStatuses = [
      { id: '', name: t('status.status_placeholder') },
      { id: 'completed', name: t('status.completed') },
      { id: 'pending', name: t('status.pending') },
      { id: 'failed', name: t('status.failed') },
      { id: 'refunded', name: t('status.refunded') },
    ];

    const newFilters = [
      {
        key: 'from',
        label: t('filters.start_date'),
        type: 'date',
      },
      {
        key: 'status',
        label: t('filters.payment_status'),
        type: 'select',
        options: newStatuses.map((status) => ({
          id: status.id,
          label: status.name,
        })),
      },
    ];
    setFilters(newFilters);
  }, [i18n.language]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setFilterData((prev) => ({ ...prev, page: newPage }));
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newSize) => {
    setFilterData((prev) => ({ ...prev, size: newSize, page: 1 }));
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    if (key === 'from' || key === 'to') {
      setFilterData((prev) => ({
        ...prev,
        from: value.from,
        to: value.to,
        page: 1,
      }));
    } else {
      setFilterData((prev) => ({
        ...prev,
        [key]: value,
        page: 1,
      }));
    }
  };

  // Handle refund click
  const handleRefundClick = (order) => {
    setRefundAmount(parseFloat(order.amount));
    setOpenDialog(true);
    setSelectedOrder(order);
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setSelectedOrder(null);
    setLoading(false);
    setRefundAmount(0);
    setOpenDialog(false);
  };

  // Handle refund submit
  const handleRefundSubmit = () => {
    setLoading(true);
    refundPayment(selectedOrder.id, refundAmount, description)
      .then((res) => {
        setNotification({
          show: true,
          snackbarMessage: 'Amount refunded successfully!',
          severity: 'success',
        });
        setOpenDialog(false);
        setLoading(false);
        refreshList();
      })
      .catch((e) => {
        setNotification({
          show: true,
          snackbarMessage: e.response?.data?.message || 'An error occurred. Please try again.',
          severity: 'error',
        });
        setOpenDialog(false);
        setLoading(false);
      });
  };

  // Function to fetch and refresh the orders list
  const refreshList = () => {
    const queryParams = formatDataToUrlQueryParams(filterData);
    getOrdersList(`?${queryParams}`)
      .then((res) => {
        const formattedRows = res.data.orders.map((order) => {
          const row = {
            [t('table.OrderID')]: order.order_id ?? 'NA',
            [t('table.Date')]: order.date,
            ...(order.user_email ? { Email: order.user_email.toLowerCase() } : {}),
            [t('table.PaymentMethod')]: <>{getStatusIcon(order?.payment_method)}</>,
            [t('table.DocumentTitle')]: order.articles.map((article) => article.title).join(', '),
            [t('table.Pages')]: order.articles.map((article) => article.page_count).join(', '),
            [t('table.ExecutedServices')]: (
              <ServicesIconsGroup
                stepsData={
                  order.executed_services?.split(',').map((s) => ({
                    service_name: s.trim(),
                    service_status: 'COMPLETED',
                  })) || []
                }
              />
            ),
            [t('table.Status')]: <>{getStatusIcon(order?.status, i18n.language)}</>,
            [t('table.Amount')]: `$${order.amount}`,
            [t('table.RefundedAmount')]: order.refunded_amount > 0 
            ? `$${order.refunded_amount}`
            : '-',
          };

          const actions = [];
          if (order.download_invoice) {
            actions.push(
              <Tooltip title={t('downloadInvoice')} key="download">
                <span>
                  <FaDownload
                    onClick={() => downloadInvoicePDF(order, profileData, logo, t, i18n.language)}
                  />
                </span>
              </Tooltip>
            );
          }

          if (role === SUPER_ADMIN_ROLE && order?.status === 'COMPLETED' && (order?.payment_method == 'paypal' || order?.payment_method == 'credit-card')) {
            actions.push(
              <Button
                title="Refund"
                key="refund"
                onClick={() => handleRefundClick(order)}
              >
                {getStatusIcon('REFUNDED-ICON')}
              </Button>
            );
          }

          row[t('table.Action')] = <>{actions}</>;
          return row;
        });

        setRows(formattedRows);
        setLoading(false);
        setOrdersCount(res.data.count);
      })
      .catch((error) => {
        console.error('Error fetching orders:', error);
        setLoading(false);
      });
  };

  // Fetch the orders list
  useEffect(() => {
    refreshList();
  }, [filterData, profileData, i18n.language]);

  // Fetch the order insights data
  useEffect(() => {
    if (rolesViewCards.includes(role)) {
      getOrdersReport()
        .then((response) => {
          setOrderData({
            totalOrders: response.data.total_orders,
            ordersInProcess: response.data.orders_in_process,
            ordersCompleted: response.data.orders_completed,
            totalRevenue: response.data.total_revenue || 0,
            percentageChanges: response.data.percentage_changes || {},
          });
          setOrderDataLoading(false);
        })
        .catch((error) => {
          console.error('Failed to fetch order data:', error);
          setOrderDataLoading(false);
        });
    } else {
      setOrderDataLoading(false);
    }
  }, [role]);

  // Define the data for the cards
  const allCardsData = [
    {
      title: { text: t('total_orders'), fontWeight: 'medium' },
      count: orderData.totalOrders,
      percentage: isFreeAgent
        ? null
        : {
            color:
              orderData.percentageChanges.total_orders > 0
                ? 'success'
                : orderData.percentageChanges.total_orders < 0
                ? 'error'
                : 'warning',
            text:
              orderData.percentageChanges.total_orders != null
                ? `${Math.abs(orderData.percentageChanges.total_orders).toFixed(2)}%`
                : '0.0%',
            icon:
              orderData.percentageChanges.total_orders > 0 ? (
                <TrendingUpIcon fontSize="small" sx={{ color: 'green' }} />
              ) : orderData.percentageChanges.total_orders < 0 ? (
                <TrendingDownIcon fontSize="small" sx={{ color: 'red' }} />
              ) : (
                <MdOutlineRemove fontSize="small" color="gray" />
              ),
          },
      icon: { color: 'primary', component: <HiOutlineClipboardDocumentCheck /> },
      direction: 'left',
      comparisonText: isFreeAgent ? null : 'Compared to yesterday',
    },
    {
      title: { text: t('orders_in_process'), fontWeight: 'medium' },
      count: orderData.ordersInProcess,
      percentage: isFreeAgent
        ? null
        : {
            color:
              orderData.percentageChanges.orders_in_process > 0
                ? 'success'
                : orderData.percentageChanges.orders_in_process < 0
                ? 'error'
                : 'warning',
            text:
              orderData.percentageChanges.orders_in_process != null
                ? `${Math.abs(orderData.percentageChanges.orders_in_process).toFixed(2)}%`
                : '0.0%',
            icon:
              orderData.percentageChanges.orders_in_process > 0 ? (
                <TrendingUpIcon fontSize="small" sx={{ color: 'green' }} />
              ) : orderData.percentageChanges.orders_in_process < 0 ? (
                <TrendingDownIcon fontSize="small" sx={{ color: 'red' }} />
              ) : (
                <MdOutlineRemove fontSize="small" color="gray" />
              ),
          },
      icon: { color: 'info', component: <GrInProgress /> },
      direction: 'left',
      comparisonText: isFreeAgent ? null : 'Compared to yesterday',
    },
    {
      title: { text: t('orders_completed'), fontWeight: 'medium' },
      count: orderData.ordersCompleted,
      percentage: isFreeAgent
        ? null
        : {
            color:
              orderData.percentageChanges.orders_completed > 0
                ? 'success'
                : orderData.percentageChanges.orders_completed < 0
                ? 'error'
                : 'warning',
            text:
              orderData.percentageChanges.orders_completed != null
                ? `${Math.abs(orderData.percentageChanges.orders_completed).toFixed(2)}%`
                : '0.0%',
            icon:
              orderData.percentageChanges.orders_completed > 0 ? (
                <TrendingUpIcon fontSize="small" sx={{ color: 'green' }} />
              ) : orderData.percentageChanges.orders_completed < 0 ? (
                <TrendingDownIcon fontSize="small" sx={{ color: 'red' }} />
              ) : (
                <MdOutlineRemove fontSize="small" color="gray" />
              ),
          },
      icon: { color: 'warning', component: <MdDone /> },
      direction: 'left',
      comparisonText: isFreeAgent ? null : 'Compared to yesterday',
    },
    {
      title: { text: t('total_revenue'), fontWeight: 'medium' },
      count: `$${orderData.totalRevenue.toFixed(2)}`,
      percentage: isFreeAgent
        ? null
        : {
            color:
              orderData.percentageChanges.total_revenue > 0
                ? 'success'
                : orderData.percentageChanges.total_revenue < 0
                ? 'error'
                : 'warning',
            text:
              orderData.percentageChanges.total_revenue != null
                ? `${Math.abs(orderData.percentageChanges.total_revenue).toFixed(2)}%`
                : '0.0%',
            icon:
              orderData.percentageChanges.total_revenue > 0 ? (
                <TrendingUpIcon fontSize="small" sx={{ color: 'green' }} />
              ) : orderData.percentageChanges.total_revenue < 0 ? (
                <TrendingDownIcon fontSize="small" sx={{ color: 'red' }} />
              ) : (
                <MdOutlineRemove fontSize="small" color="gray" />
              ),
          },
      icon: { color: 'error', component: <MdOutlineAttachMoney /> },
      direction: 'left',
      comparisonText: isFreeAgent ? null : 'Compared to yesterday',
    },
  ];

  // Filter the cards based on the role
  const cardsData = allCardsData.filter((card) => {
    if (card.title.text === "Total Revenue" || card.title.text === "إجمالي الإيرادات") {
      return rolesCanSeeRevenue.includes(role);
    }
    return rolesViewCards.includes(role);
  });

  return (
    <>
      <Tables
        rows={rows}
        title={`${t('table.title')} (${ordersCount})`}
        loadingData={loading}
        size={filterData.size}
        page={filterData.page}
        handlePageChange={handlePageChange}
        handleRowsPerPageChange={handleRowsPerPageChange}
        count={ordersCount}
        filters={filters}
        filterData={filterData}
        handleFilterChange={handleFilterChange}
        cardsData={cardsData}
        cardsLoading={orderDataLoading}
      />

      <RefundDialog
        open={openDialog}
        onClose={handleCloseDialog}
        refundType={refundType}
        setRefundType={setRefundType}
        refundAmount={refundAmount}
        setRefundAmount={setRefundAmount}
        selectedOrder={selectedOrder}
        description={description}
        setDescription={setDescription}
        onSubmit={handleRefundSubmit}
        loading={loading}
      />

      <SnackbarNotification
        notification={notification}
        setNotification={setNotification}
      />
    </>
  );
};

export default PaymentList;
